/* eslint-disable */
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { getData } from "utils/Gateway";
import * as url from "helper/UrlHelper";

import TabsHeader from "components/Common/TabsHeader/TabsHeader";
import CrmLeadDashboardHeader from "../Header/CrmLeadDashboardHeader";
import AlertNotification from "components/Common/AlertNotification/AlertNotification";
import { crmCommonHeaderLinkLists } from "helper/CrmHelper/CrmHelper";
import AccessDeniedView from "components/Common/AccessDeniedView/AccessDeniedView";
import LoadingSkeleton from "components/Common/ListLoadingSkeleton/LoadingSkeleton";

/**
 * @component DashboardCard
 * @description A reusable card component to display a summary of key metrics like leads, opportunities, etc.
 * It shows a total count, current month's count, and the percentage change from the previous month.
 * @param {string} title - The title of the card.
 * @param {Array<Object>} items - An array of data objects for the card statistics.
 * @param {string} cardType - A string ('leads', 'following', etc.) to determine the card's color scheme.
 */
const DashboardCard = ({ title, items, cardType }) => {
  // Hook for handling translations.
  const { t } = useTranslation();

  // --- STYLING MAPPINGS ---
  // Defines background colors for different card types for visual distinction.
  const cardBg = {
    leads: "rgb(130 128 255 / 20%)",
    following: "rgb(40 167 69 / 20%)",
    opportunities: "rgb(254 197 61 / 20%)",
    notes: "rgb(23 162 184 / 20%)",
  };
  // Defines icon and text colors corresponding to each card type.
  const cardColor = {
    leads: "#8280FF",
    following: "#28a745",
    opportunities: "#FEC53D",
    notes: "#17a2b8",
  };

  // --- DATA EXTRACTION & CALCULATION ---
  // Extracts the total count from the first item in the props array.
  const totalCount = items[0]?.value ?? 0;
  // Extracts the count for the current month.
  const currentMonthCount = items[1]?.value ?? 0;
  // Extracts the count for the previous month.
  const prevMonthCount = items[2]?.value ?? 0;
  // Calculates the percentage change between the current and previous months.
  const percentChange =
    prevMonthCount > 0
      ? (((currentMonthCount - prevMonthCount) / prevMonthCount) * 100).toFixed(
          2
        )
      : currentMonthCount > 0
      ? 100
      : 0; // Handle division by zero case.

  // --- COMPONENT RENDER ---
  return (
    <div className="col-lg-3 col-md-6 mb-4 mb-lg-0">
      {/* Main card container with dynamic styling */}
      <div className="project h-100 bg-gray-200 p-3 p-lg-4 d-flex gap-3 rounded-10 shadow-sm">
        {/* Information section for text content */}
        <div className="info flex-fill">
          <p className="fw-semibold mb-3">{t(title)}</p>
          <div className="d-flex align-items-end gap-1 mb-1">
            <h2 className="fw-bold lh-1 mb-0">{currentMonthCount}</h2>
            <h3 className="text-gray fw-bold lh-1 mb-1">
              /{totalCount} {t(title)}
            </h3>
          </div>
          {/* Section to display the percentage change trend */}
          <p className="fs-xs d-flex align-items-center">
            <span
              className={`d-block material-symbols-outlined icon-md me-1 ${
                percentChange >= 0 ? "text-success" : "text-danger"
              }`}
            >
              {percentChange >= 0 ? "trending_up" : "trending_down"}
            </span>
            <span
              className={`d-block ${
                percentChange >= 0 ? "text-success" : "text-danger"
              }`}
            >
              {Math.abs(percentChange)}% from {prevMonthCount} {t(title)} last
              month
            </span>
          </p>
        </div>
        {/* Icon section with dynamic background and color */}
        <div
          className="icon flex-shrink-0 d-flex align-items-center justify-content-center rounded-18"
          style={{
            width: "3.75rem",
            height: "3.75rem",
            backgroundColor: cardBg[cardType] || "#eee",
            color: cardColor[cardType] || "#333",
          }}
        >
          <span
            className="d-block material-symbols-outlined icon-fill"
            style={{ fontSize: "2rem" }}
          >
            {items[0]?.icon || "dashboard"}
          </span>
        </div>
      </div>
    </div>
  );
};

/**
 * @component RankCard
 * @description A specialized card component to display user rank information.
 * @param {string} title - The title of the card.
 * @param {Array<Object>} items - An array of rank data objects to be listed.
 * @param {string} cardType - A string ('ranks') to determine the card's color scheme.
 */
const RankCard = ({ title, items, cardType }) => {
  // Hook for handling translations.
  const { t } = useTranslation();

  // --- STYLING MAPPINGS ---
  const cardBg = {
    ranks: "rgb(111 66 193 / 20%)",
  };
  const cardColor = {
    ranks: "#6f42c1",
  };

  // --- COMPONENT RENDER ---
  return (
    <div className="col-lg-3 col-md-6 mb-4 mb-lg-0">
      <div className="project h-100 bg-gray-200 p-3 p-lg-4 d-flex gap-3 rounded-10 shadow-sm">
        {/* Information section for listing ranks */}
        <div className="info flex-fill">
          <p className="fw-semibold mb-3">{t(title)}</p>
          {/* Map through the items array to display each rank */}
          {items.map((item, index) => (
            <div
              key={index}
              className="d-flex align-items-center gap-1"
              style={{ marginBottom: "10px" }}
            >
              <span
                className="d-block material-symbols-outlined icon-fill"
                style={{
                  fontSize: "1rem",
                  color: cardColor[cardType] || "#333",
                }}
              >
                bookmark_star
              </span>
              <h4 className="fw-bold lh-1 mb-0 text-secondary">
                {t(item.label)} : {item.value ?? "-"}
              </h4>
            </div>
          ))}
        </div>
        {/* Icon section for the rank card */}
        <div
          className="icon flex-shrink-0 d-flex align-items-center justify-content-center rounded-18"
          style={{
            width: "3.75rem",
            height: "3.75rem",
            backgroundColor: cardBg[cardType] || "#eee",
            color: cardColor[cardType] || "#333",
          }}
        >
          <span
            className="d-block material-symbols-outlined icon-fill"
            style={{ fontSize: "2rem" }}
          >
            military_tech
          </span>
        </div>
      </div>
    </div>
  );
};

/**
 * @component CrmLeadDashboardBody
 * @description This is the main component for the CRM Lead Dashboard. It fetches dashboard data,
 * handles loading and error states, and renders the layout including headers and data cards.
 */
const CrmLeadDashboardBody = () => {
  // --- HOOKS & CONTEXT ---
  const { t } = useTranslation(); // For internationalization.
  // Retrieving user and session information from local storage.
  const moduleAccess = localStorage.getItem("moduleaccess");
  const token = localStorage.getItem("token");
  const userInfo = JSON.parse(localStorage.getItem("userInfo"));
  // Helper to generate navigation links for the header.
  const commonHeaderObject = crmCommonHeaderLinkLists(t);

  // --- STATE MANAGEMENT ---
  // State to manage the loading status while fetching data.
  const [isLoading, setIsLoading] = useState(false);
  // State to store the dashboard data fetched from the API.
  const [dashboardData, setDashboardData] = useState({});
  // States for managing the alert notification visibility, message, and type.
  const [showAlert, setShowAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [messageType, setMessageType] = useState("");

  // --- DATA FETCHING ---
  /**
   * @function leadDashboardDetails
   * @description Fetches lead dashboard statistics from the API.
   * It handles setting the loading state and managing success or error responses.
   */
  const leadDashboardDetails = async () => {
    // Action: Set loading to true before starting the API call.
    setIsLoading(true);
    try {
      const requestURL = `${url.API_BASE_URL}${url.API_GET_LEAD_DASHBOARD}?token=${token}`;
      const response = await getData(requestURL);

      // Action: Handle the API response.
      if (response.status) {
        setDashboardData(response.data);
      } else {
        // Handle API error response by showing an alert.
        setMessageType("error");
        setAlertMessage(response.message || "Failed to fetch dashboard data.");
        setShowAlert(true);
      }
    } catch (error) {
      // Handle unexpected errors during the fetch operation.
      setMessageType("error");
      setAlertMessage(error.message || "An unexpected error occurred.");
      setShowAlert(true);
    } finally {
      // Action: Set loading to false after the API call is complete.
      setIsLoading(false);
    }
  };

  // --- LIFECYCLE HOOK ---
  // `useEffect` hook to call `leadDashboardDetails` once when the component mounts.
  useEffect(() => {
    leadDashboardDetails();
  }, []);

  // --- EVENT HANDLERS ---
  /**
   * @function onAlertClose
   * @description Closes the alert notification and resets its state.
   */
  const onAlertClose = () => {
    setShowAlert(false);
    setAlertMessage("");
    setMessageType("");
  };

  // --- DATA STRUCTURING ---
  // Preparing data arrays to be passed as props to the DashboardCard components.
  const leadDetails = [
    { label: "Total", value: dashboardData?.totalleadcount, icon: "functions" },
    { label: "Current Month", value: dashboardData?.currentmonthleadcount },
    { label: "Previous Month", value: dashboardData?.previousmonthleadcount },
  ];

  const followingDetails = [
    {
      label: "Total",
      value: dashboardData?.followingleadcount,
      icon: "person_add",
    },
    { label: "Current Month", value: dashboardData?.currentmonthfollowcount },
    { label: "Previous Month", value: dashboardData?.prevmonthfollowcount },
  ];

  const opportunityDetails = [
    { label: "Total", value: dashboardData?.opportunitiescount, icon: "star" },
    {
      label: "Current Month",
      value: dashboardData?.opportunitiescurrentmonthcount,
    },
    {
      label: "Previous Month",
      value: dashboardData?.opportunitiesprevmonthcount,
    },
  ];

  const noteDetails = [
    { label: "Total", value: dashboardData?.notescount, icon: "note_alt" },
    { label: "Current Month", value: dashboardData?.currentmonthnotecount },
    { label: "Previous Month", value: dashboardData?.prevmonthnotecount },
  ];

  // Preparing data array for the RankCard component.
  const rankDetails = [
    { label: "Rank 100", value: dashboardData?.leadrankhundredcount },
    { label: "Rank 99", value: dashboardData?.leadrankninetyninecount },
    { label: "Rank 98", value: dashboardData?.leadrankninetyeightcount },
  ];

  // --- ACCESS CONTROL ---
  // Check if the user has the required permissions to view this page.
  if (
    !userInfo ||
    !(
      userInfo.role.slug === "ADMIN" ||
      userInfo.role.slug === "SUPER_ADMIN" ||
      (moduleAccess.includes("MOD_CRM") && moduleAccess.includes("MOD_LEAD"))
    )
  ) {
    // Renders an 'Access Denied' view if permissions are not met.
    return <AccessDeniedView />;
  }

  // --- COMPONENT RENDER ---
  return (
    // Main content wrapper for the dashboard.
    <div id="content_wrapper">
      <section className="dashboard-wrapper bg-white pb-5">
        {/* Component for rendering the main navigation tabs */}
        <TabsHeader
          commonHeaderObject={commonHeaderObject}
          activeOption={t("Leads")}
        />

        {/* Main container for the dashboard content */}
        <div className="container-fluid px-lg-5">
          {/* Header specific to the CRM Lead Dashboard, with a reload button */}
          <CrmLeadDashboardHeader reloadView={leadDashboardDetails} />

          {/* Conditional rendering: Show loading skeleton or dashboard content */}
          {isLoading ? (
            <LoadingSkeleton />
          ) : (
            <div>
              {/* Row containing the primary metric cards */}
              <div className="row mb-4">
                <DashboardCard
                  title="Leads"
                  items={leadDetails}
                  cardType="leads"
                />
                <DashboardCard
                  title="Following"
                  items={followingDetails}
                  cardType="following"
                />
                <DashboardCard
                  title="Opportunities"
                  items={opportunityDetails}
                  cardType="opportunities"
                />
                <DashboardCard
                  title="Notes"
                  items={noteDetails}
                  cardType="notes"
                />
              </div>
              {/* Row containing the specialized rank card */}
              <div className="row mb-2">
                <RankCard title="Ranks" items={rankDetails} cardType="ranks" />
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Renders the alert notification if showAlert is true */}
      {showAlert && (
        <AlertNotification
          showAlert={showAlert}
          message={alertMessage}
          alertType={messageType}
          onClose={onAlertClose}
        />
      )}
    </div>
  );
};

export default CrmLeadDashboardBody;
